/**
 * Forms Library Management
 * Admin UI for managing legal forms, versions, approvals, and kill-switch
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getEnv } from '@/lib/env';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { Card } from '@/components/ui/card';
import { useFormUpload } from '@/hooks/useFormUpload';
import VersionHistoryModal from '@/components/forms/VersionHistoryModal';
import ApprovalCard from '@/components/forms/ApprovalCard';
import FormAnalytics from '@/components/forms/FormAnalytics';
import { toast } from 'sonner';

const { supabaseUrl, supabaseAnonKey } = getEnv();

interface Form {
  id: string;
  form_code: string;
  form_name: string;
  form_category: string;
  current_version?: any;
  total_versions: number;
  approved_versions: number;
}

export default function FormsLibraryManagement() {
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'catalog' | 'upload' | 'approvals' | 'analytics' | 'kill-switch'>('catalog');
  
  // Version history modal
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  
  // Upload form state
  const [uploadData, setUploadData] = useState({
    formCode: '',
    versionNumber: '',
    effectiveDate: '',
    expirationDate: '',
    applicableStates: [] as string[],
    changeNotes: '',
    regulatoryRef: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Kill-switch state
  const [killSwitchData, setKillSwitchData] = useState({
    versionId: '',
    reason: '',
    complianceIssue: '',
  });

  // Pending approvals
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);

  const { uploading, progress, submitFormVersion, approveVersion, rejectVersion, activateKillSwitch } = useFormUpload();

  useEffect(() => {
    loadForms();
  }, []);

  useEffect(() => {
    if (view === 'approvals') {
      loadPendingApprovals();
    }
  }, [view]);

  const loadForms = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/forms-library-api/list`, {
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${supabaseAnonKey}`,
        }
      });
      const data = await response.json();
      setForms(data.forms || []);
    } catch (error) {
      console.error('Error loading forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingApprovals = async () => {
    setApprovalsLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_versions')
        .select('*, form_code')
        .eq('status', 'pending_approval')
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setPendingApprovals(data || []);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    } finally {
      setApprovalsLoading(false);
    }
  };

  const handleApprove = async (versionId: string, comments: string) => {
    const result = await approveVersion(versionId, comments);
    if (result.success) {
      toast.success(result.message);
      await loadPendingApprovals();
      await loadForms();
    } else {
      toast.error(result.message);
    }
  };

  const handleReject = async (versionId: string, reason: string) => {
    const result = await rejectVersion(versionId, reason);
    if (result.success) {
      toast.success(result.message);
      await loadPendingApprovals();
      await loadForms();
    } else {
      toast.error(result.message);
    }
  };

  const handleViewVersions = (form: Form) => {
    setSelectedForm(form);
    setVersionModalOpen(true);
  };

  const handleUploadNew = (formCode: string) => {
    setUploadData({ ...uploadData, formCode });
    setView('upload');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = Array.from(e.target.selectedOptions);
    const states = options.map(opt => opt.value);
    setUploadData({ ...uploadData, applicableStates: states });
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = await submitFormVersion(uploadData, selectedFile);
    
    if (result.success) {
      toast.success(result.message);
      // Reset form
      setUploadData({
        formCode: '',
        versionNumber: '',
        effectiveDate: '',
        expirationDate: '',
        applicableStates: [],
        changeNotes: '',
        regulatoryRef: '',
      });
      setSelectedFile(null);
      // Reload forms and switch to catalog
      await loadForms();
      setView('catalog');
    } else {
      toast.error(result.message);
    }
  };

  const handleKillSwitchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!killSwitchData.versionId || !killSwitchData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    const confirmed = window.confirm(
      'Are you absolutely sure you want to activate the kill-switch? This will immediately disable this form version and cannot be undone.'
    );

    if (!confirmed) return;

    const result = await activateKillSwitch(
      killSwitchData.versionId,
      killSwitchData.reason,
      killSwitchData.complianceIssue
    );

    if (result.success) {
      toast.success(result.message);
      setKillSwitchData({ versionId: '', reason: '', complianceIssue: '' });
      await loadForms();
    } else {
      toast.error(result.message);
    }
  };

  return (
    <AdminPageLayout
      title="Forms Library"
      subtitle="Manage legal forms, versions, and compliance"
    >

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setView('catalog')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              view === 'catalog'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Forms Catalog
          </button>
          <button
            onClick={() => setView('upload')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              view === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upload New Version
          </button>
          <button
            onClick={() => setView('approvals')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              view === 'approvals'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Approvals
          </button>
          <button
            onClick={() => setView('analytics')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              view === 'analytics'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Analytics
          </button>
          <button
            onClick={() => setView('kill-switch')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              view === 'kill-switch'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🚨 Kill-Switch
          </button>
        </nav>
      </div>

      {/* Content */}
      {view === 'catalog' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Forms Catalog</h2>
          <div className="divide-y divide-gray-200 -mx-6">
            {loading ? (
              <div className="p-12 text-center text-gray-500">Loading forms...</div>
            ) : forms.length === 0 ? (
              <div className="p-12 text-center text-gray-500">No forms found</div>
            ) : (
              forms.map(form => (
                <div key={form.id} className="px-6 py-6 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">{form.form_code}</h3>
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {form.form_category}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-1">{form.form_name}</p>
                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                          <span>Versions: {form.total_versions}</span>
                          <span>Approved: {form.approved_versions}</span>
                          {form.current_version && (
                            <span className="text-green-600 font-medium">
                              Current: v{form.current_version.version_number}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleViewVersions(form)}
                          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          View Versions
                        </button>
                        <button 
                          onClick={() => handleUploadNew(form.form_code)}
                          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
                        >
                          Upload New
                        </button>
                      </div>
                    </div>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {view === 'upload' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-6">Upload New Form Version</h2>
          <form onSubmit={handleUploadSubmit} className="space-y-6 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form Code *
              </label>
              <select 
                value={uploadData.formCode}
                onChange={(e) => setUploadData({ ...uploadData, formCode: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select form...</option>
                {forms.map(form => (
                  <option key={form.id} value={form.form_code}>
                    {form.form_code} - {form.form_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Version Number *
              </label>
              <input
                type="text"
                value={uploadData.versionNumber}
                onChange={(e) => setUploadData({ ...uploadData, versionNumber: e.target.value })}
                required
                placeholder="e.g., 1.1, 2.0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload PDF/DOCX *
              </label>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Effective Date *
                </label>
                <input
                  type="date"
                  value={uploadData.effectiveDate}
                  onChange={(e) => setUploadData({ ...uploadData, effectiveDate: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={uploadData.expirationDate}
                  onChange={(e) => setUploadData({ ...uploadData, expirationDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicable States * (Hold Ctrl/Cmd to select multiple)
              </label>
              <select 
                multiple 
                value={uploadData.applicableStates}
                onChange={handleStateChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg h-24"
              >
                <option value="FL">Florida</option>
                <option value="TX">Texas</option>
                <option value="CA">California</option>
                <option value="NY">New York</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Changes from Previous Version *
              </label>
              <textarea
                rows={3}
                value={uploadData.changeNotes}
                onChange={(e) => setUploadData({ ...uploadData, changeNotes: e.target.value })}
                required
                placeholder="Describe what changed..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Regulatory Reference
              </label>
              <input
                type="text"
                value={uploadData.regulatoryRef}
                onChange={(e) => setUploadData({ ...uploadData, regulatoryRef: e.target.value })}
                placeholder="e.g., FL Stat. §634.041"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {uploading && progress > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">Uploading...</span>
                  <span className="text-sm text-blue-700">{progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload & Submit for Approval'}
            </button>
          </form>
        </Card>
      )}

      {view === 'approvals' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Pending Approvals</h2>
              <p className="text-sm text-gray-600 mt-1">
                {pendingApprovals.length} {pendingApprovals.length === 1 ? 'version' : 'versions'} awaiting review
              </p>
            </div>
          </div>

          {approvalsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading pending approvals...</p>
              </div>
            </div>
          ) : pendingApprovals.length === 0 ? (
            <Card className="p-12 text-center text-gray-500">
              <p>No pending approvals</p>
              <p className="text-sm mt-2">New form versions awaiting approval will appear here</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((version) => {
                const form = forms.find(f => f.form_code === version.form_code);
                return (
                  <ApprovalCard
                    key={version.id}
                    version={version}
                    formName={form?.form_name || version.form_code}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === 'kill-switch' && (
        <Card className="p-6 bg-red-50 border-red-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg className="h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-red-900 mb-2">Emergency Kill-Switch</h2>
              <p className="text-red-700 mb-4">
                Use this to immediately disable a non-compliant form version. This action is <strong>irreversible</strong> and should only be used when a form contains errors that could create legal liability.
              </p>
              
              <div className="bg-white rounded-lg p-6 border border-red-200">
                <h3 className="font-semibold mb-4">Activate Kill-Switch</h3>
                <form onSubmit={handleKillSwitchSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Form Version to Disable
                    </label>
                    <select 
                      value={killSwitchData.versionId}
                      onChange={(e) => setKillSwitchData({ ...killSwitchData, versionId: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select form version...</option>
                      {/* TODO: Load approved versions from database */}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Disabling *
                    </label>
                    <textarea
                      rows={3}
                      value={killSwitchData.reason}
                      onChange={(e) => setKillSwitchData({ ...killSwitchData, reason: e.target.value })}
                      required
                      placeholder="Explain why this form must be disabled immediately..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Compliance Issue
                    </label>
                    <input
                      type="text"
                      value={killSwitchData.complianceIssue}
                      onChange={(e) => setKillSwitchData({ ...killSwitchData, complianceIssue: e.target.value })}
                      placeholder="Legal citation or regulatory concern"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> Activating the kill-switch will immediately prevent this form version from being used in any new agreements. Existing agreements using this form will be flagged for review.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={uploading}
                    className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    🚨 ACTIVATE KILL-SWITCH
                  </button>
                </form>
              </div>

              <div className="mt-6 bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="font-semibold mb-4">Active Kill-Switches</h3>
                <p className="text-sm text-gray-500">No forms currently disabled</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {view === 'analytics' && (
        <FormAnalytics />
      )}

      {/* Version History Modal */}
      {selectedForm && (
        <VersionHistoryModal
          formCode={selectedForm.form_code}
          formName={selectedForm.form_name}
          isOpen={versionModalOpen}
          onClose={() => setVersionModalOpen(false)}
        />
      )}
    </AdminPageLayout>
  );
}
